import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mic, Newspaper, Briefcase, ShieldAlert, Play, Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_ICON = {
  chella: Newspaper,
  michael: Briefcase,
  marcus: ShieldAlert,
};

export default function Newsroom() {
  const [roster, setRoster] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [busy, setBusy] = useState("");
  const [audio, setAudio] = useState({}); // avatarId -> base64 mp3 src or "no_audio"
  const [reason, setReason] = useState({});

  useEffect(() => {
    axios.get(`${API}/avatars`).then((r) => {
      setRoster(r.data.avatars || []);
      setAudioEnabled(!!r.data.audio_enabled);
    }).catch(() => {});
  }, []);

  const sample = async (avatar) => {
    const text = `${avatar.name} here from the Newseze newsroom. ${avatar.bio} ${avatar.tagline}`;
    setBusy(avatar.id);
    setReason((r) => ({ ...r, [avatar.id]: null }));
    try {
      const r = await axios.post(`${API}/avatars/audio-brief`, { avatar_id: avatar.id, text });
      if (r.data.audio_base64) {
        setAudio((a) => ({ ...a, [avatar.id]: `data:audio/mpeg;base64,${r.data.audio_base64}` }));
      } else {
        setReason((rr) => ({ ...rr, [avatar.id]: r.data.reason || "audio_unavailable" }));
      }
    } catch (e) {
      setReason((rr) => ({ ...rr, [avatar.id]: e?.response?.data?.detail || "audio_failed" }));
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <Header />
      <main className="max-w-[1200px] mx-auto px-5 py-8">
        <section className="rounded-2xl bg-gradient-to-br from-[#0a1f3d] to-[#1e40af] text-white p-8 md:p-10 relative overflow-hidden" data-testid="newsroom-hero">
          <div className="inline-flex items-center gap-2 text-[var(--gold)] text-[11px] uppercase tracking-[0.2em] font-black mb-3">
            <Mic size={14} /> The Newseze Newsroom
          </div>
          <h1 className="text-[36px] md:text-[48px] font-black tracking-[-0.025em] leading-[1.05]">
            Three anchors. <span className="text-[var(--gold)]">One calm voice.</span>
          </h1>
          <p className="text-[15px] md:text-[17px] opacity-95 mt-3 leading-snug max-w-[680px]">
            Newseze stories are voiced by three on-air avatars — each scoped to subjects where their tone fits the room. <b className="text-[var(--gold)]">No drama. No conspiracies.</b> Just calm, sourced, balanced news.
          </p>
        </section>

        <section className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="newsroom-roster">
          {(roster || []).map((a) => {
            const Icon = ROLE_ICON[a.id] || Mic;
            return (
              <div key={a.id} className="rounded-2xl border border-[var(--line)] bg-white overflow-hidden flex flex-col" data-testid={`avatar-card-${a.id}`}>
                <div className="aspect-[4/3] bg-gradient-to-br" style={{ backgroundColor: a.color }}>
                  <img src={a.portrait} alt={a.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2">
                    <Icon size={16} className="text-[var(--blue)]" />
                    <h2 className="text-[24px] font-black tracking-[-0.02em] text-[var(--ink)]">{a.name}</h2>
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--gold)] mt-1">{a.role}</div>
                  <p className="text-[13.5px] text-[var(--ink)] leading-snug mt-2">{a.bio}</p>
                  <div className="text-[12px] text-[var(--muted)] mt-2 italic">"{a.tagline}"</div>
                  <div className="flex flex-wrap gap-1 mt-3">
                    {(a.covers || []).map((c) => (
                      <span key={c} className="text-[9px] uppercase tracking-[0.14em] font-black bg-[var(--sky)] text-[var(--blue)] px-2 py-0.5 rounded" data-testid={`avatar-covers-${a.id}-${c}`}>{c}</span>
                    ))}
                  </div>
                  <button
                    onClick={() => sample(a)}
                    disabled={busy === a.id || !audioEnabled}
                    className="btn primary mt-4 w-full justify-center"
                    data-testid={`avatar-sample-${a.id}`}
                  >
                    {busy === a.id ? <Loader2 size={14} className="animate-spin" /> : (
                      <><Play size={14} /> Hear {a.name.split(" ")[0]}'s voice</>
                    )}
                  </button>
                  {!audioEnabled && (
                    <div className="text-[11px] text-[var(--muted)] mt-2 italic" data-testid={`avatar-audio-disabled-${a.id}`}>
                      Audio briefs activate once the operator wires the ElevenLabs key.
                    </div>
                  )}
                  {audio[a.id] && (
                    <audio controls src={audio[a.id]} className="mt-3 w-full" data-testid={`avatar-audio-${a.id}`} />
                  )}
                  {reason[a.id] && (
                    <div className="text-[11.5px] text-[var(--red,#b91c1c)] mt-2" data-testid={`avatar-audio-reason-${a.id}`}>
                      {reason[a.id] === "daily_cap_reached" ? "Daily audio cap reached — try again tomorrow." :
                       reason[a.id] === "elevenlabs_key_missing" ? "Voice generation not yet activated by the operator." :
                       reason[a.id]}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </section>

        <section className="mt-10 rounded-2xl bg-[var(--sky)] border border-[var(--sky-2)] p-6 md:p-8" data-testid="newsroom-pledge">
          <h3 className="text-[22px] font-black tracking-[-0.02em] text-[var(--ink)]">The Newseze Editorial Pledge</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 text-[13.5px] text-[var(--ink)]">
            <li className="flex items-start gap-2"><span className="text-[var(--gold)] font-black">•</span> Calm, balanced delivery — every avatar trained to read without spin.</li>
            <li className="flex items-start gap-2"><span className="text-[var(--gold)] font-black">•</span> No drama. No conspiracies. No clickbait headlines.</li>
            <li className="flex items-start gap-2"><span className="text-[var(--gold)] font-black">•</span> Respect for every public official, regardless of party.</li>
            <li className="flex items-start gap-2"><span className="text-[var(--gold)] font-black">•</span> Sources cited, evidence ranked, opinions clearly labeled.</li>
          </ul>
          <Link to="/legal" className="btn mt-5" data-testid="newsroom-read-pledge">Read the full editorial standards</Link>
        </section>
      </main>
      <Footer />
    </>
  );
}
